import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  fetchUserById,
  updateUser,
  clearSelectedEntity,
  clearFormErrors,
  resetSubmissionStatus,
  updateFormData,
  selectSelectedUser,
  selectInitialFormData,
  selectUserDetailStatus,
  selectUserUpdateStatus,
  selectUserDetailError,
  selectUserUpdateError,
  selectUserFormErrors,
} from '../../features/users/userSlice';
import { selectUserInformations } from '../../features/authSlice';
import SharedForm from '../../components/forms/SharedForm';
import userFields from '../../config/userFields';
import { showToast } from '../../utils/utils';
import Loader from '../../components/Live/Loader';

/**
 * Edit User Page
 * Full page component for editing an existing user
 * Replaces the modal-based edit flow
 */
const EditUserPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectUserInformations);

  const selectedUser = useSelector(selectSelectedUser);
  const initialFormData = useSelector(selectInitialFormData);
  const detailStatus = useSelector(selectUserDetailStatus);
  const updateStatus = useSelector(selectUserUpdateStatus);
  const detailError = useSelector(selectUserDetailError);
  const updateError = useSelector(selectUserUpdateError);
  const formErrors = useSelector(selectUserFormErrors);

  // Local form data state
  const [formData, setFormData] = useState({});

  // Fetch user data on mount
  useEffect(() => {
    if (id && user?.token) {
      dispatch(fetchUserById({ token: user.token, userId: id }));
    }

    return () => {
      dispatch(clearSelectedEntity());
    };
  }, [id, user?.token, dispatch]);

  // Sync form data with selected user
  useEffect(() => {
    if (selectedUser) {
      setFormData(selectedUser);
    }
  }, [selectedUser]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value, files } = e.target;
    const newValue = name === 'image_url' ? files?.[0] : value;
    
    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    // Update Redux state for controlled inputs
    dispatch(updateFormData({ [name]: newValue }));

    // Clear form errors when user types
    if (formErrors[name]) {
      dispatch(clearFormErrors());
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!initialFormData) {
      showToast('Error', 'Initial data not loaded', 'error');
      return;
    }

    dispatch(updateUser({
      token: user.token,
      userId: id,
      formData,
      initialData: initialFormData,
    }));
  };

  // Handle successful update
  useEffect(() => {
    if (updateStatus === 'succeeded') {
      showToast('Success', 'User updated successfully', 'success');
      dispatch(resetSubmissionStatus());
      navigate(`/dashboard/users/${id}`, { replace: true });
    }
  }, [updateStatus, dispatch, navigate, id]);

  // Handle update errors
  useEffect(() => {
    if (updateStatus === 'failed' && updateError) {
      showToast('Error', updateError, 'error');
    }
  }, [updateStatus, updateError]);

  // Handle fetch errors
  useEffect(() => {
    if (detailStatus === 'failed' && detailError) {
      showToast('Error', detailError, 'error');
      navigate('/dashboard/users', { replace: true });
    }
  }, [detailStatus, detailError, navigate]);

  const loading = detailStatus === 'loading' || updateStatus === 'loading';

  // Loading state
  if (detailStatus === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  // Error state
  if (detailStatus === 'failed') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400 mb-4">{detailError}</p>
          <button
            onClick={() => navigate('/dashboard/users')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Users
          </button>
        </div>
      </div>
    );
  }

  // No user data
  if (!selectedUser) {
    return null;
  }

  return (
    <div className="min-h-screen py-4 px-4 sm:px-6 lg:px-8">
      <div className="max-w-full mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                Edit User
              </h1>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Update user information below
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => navigate(`/dashboard/users/${id}`)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
            </div>
          </div>
        </motion.div>

        {/* Note for password */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg dark:bg-blue-900/20 dark:border-blue-800"
        >
          <p className="text-blue-800 dark:text-blue-300 text-sm">
            Note: If you want to change user password then type password and confirmation else leave them.
          </p>
        </motion.div>

        {/* Error Messages */}
        {(updateError || Object.values(formErrors).some(Boolean)) && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg dark:bg-red-900/20 dark:border-red-800"
          >
            {updateError && (
              <p className="text-red-600 dark:text-red-400 text-sm font-medium mb-2">
                {updateError}
              </p>
            )}
            {Object.entries(formErrors).map(([field, errorMsg]) => (
              errorMsg && (
                <p key={field} className="text-red-600 dark:text-red-400 text-sm">
                  {field}: {errorMsg}
                </p>
              )
            ))}
          </motion.div>
        )}

        {/* Form Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6 lg:p-8 relative" style={{ overflow: 'visible' }}
        >
          <SharedForm
            fields={userFields}
            formData={formData}
            handleChange={handleChange}
            handleSubmit={handleSubmit}
            loading={loading}
            entity="user"
            formErrors={formErrors}
          />
        </motion.div>
      </div>
    </div>
  );
};

export default EditUserPage;
